import { useState, useEffect, useRef } from "react";
import { Dialog, DialogContent, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { api } from "@/lib/api";
import { formatTZS } from "@/data/services";
import { toast } from "sonner";
import { Loader2, CheckCircle2, Smartphone, ShieldCheck } from "lucide-react";

// type: "order" | "subscription"
// item: service object (order) or plan object (subscription)
export const CheckoutDialog = ({ open, onOpenChange, type, item }) => {
  const [step, setStep] = useState("details");
  const [form, setForm] = useState({ customer_name: "", email: "", phone: "", notes: "" });
  const [refId, setRefId] = useState(null);
  const [paymentId, setPaymentId] = useState(null);
  const [receipt, setReceipt] = useState(null);
  const [loading, setLoading] = useState(false);
  const stkTimer = useRef(null);

  useEffect(() => {
    if (open) {
      setStep("details");
      setForm({ customer_name: "", email: "", phone: "", notes: "" });
      setRefId(null);
      setPaymentId(null);
      setReceipt(null);
      setLoading(false);
      if (stkTimer.current) clearTimeout(stkTimer.current);
    }
  }, [open, item]);

  // Clear any pending simulated STK-push timer on close/unmount to avoid state updates after unmount.
  useEffect(() => {
    return () => {
      if (stkTimer.current) clearTimeout(stkTimer.current);
    };
  }, []);

  if (!item) return null;
  const amount = item.price;
  const label = type === "order" ? item.name : `${item.name} Plan`;

  const submitDetails = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      let res;
      if (type === "order") {
        res = await api.post("/orders", {
          service_id: item.id,
          service_name: item.name,
          customer_name: form.customer_name,
          email: form.email,
          phone: form.phone,
          amount,
          notes: form.notes,
        });
      } else {
        res = await api.post("/subscriptions", {
          plan: item.name,
          customer_name: form.customer_name,
          email: form.email,
          phone: form.phone,
          amount,
        });
      }
      setRefId(res.data.id);
      setStep("mpesa");
    } catch (err) {
      toast.error("Could not create your request. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  const payWithMpesa = async () => {
    setLoading(true);
    try {
      const res = await api.post("/payments/mpesa/initiate", {
        reference_type: type,
        reference_id: refId,
        phone: form.phone,
        amount,
      });
      setPaymentId(res.data.payment_id);
      setStep("pending");
      // Simulate STK push wait, then confirm
      if (stkTimer.current) clearTimeout(stkTimer.current);
      stkTimer.current = setTimeout(async () => {
        try {
          const conf = await api.post("/payments/mpesa/confirm", { payment_id: res.data.payment_id });
          setReceipt(conf.data.mpesa_receipt);
          setStep("success");
        } catch (e) {
          toast.error("Payment failed. Please try again.");
          setStep("mpesa");
        }
      }, 4200);
    } catch (err) {
      toast.error("Could not initiate M-Pesa payment.");
      setLoading(false);
    }
  };

  const field = "w-full bg-brand-card border border-gray-300 rounded-sm px-4 py-3 text-sm text-brand-dark placeholder:text-gray-400 focus:outline-none focus:border-brand-blue focus:ring-1 focus:ring-brand-blue transition-colors";

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        data-testid="checkout-dialog"
        className="bg-brand-card border border-gray-200 text-brand-dark max-w-md p-0 overflow-hidden gap-0"
      >
        <DialogTitle className="sr-only">{label} checkout</DialogTitle>
        <div className="px-6 pt-6 pb-4 border-b border-gray-200 bg-brand-dark text-white">
          <p className="text-xs tracking-[0.2em] uppercase text-brand-yellow">
            {type === "order" ? "Order Service" : "Subscribe"}
          </p>
          <h3 className="text-2xl font-heading mt-1">{label}</h3>
        </div>

        {step === "details" && (
          <form onSubmit={submitDetails} className="p-6 space-y-4" data-testid="checkout-details-form">
            <input data-testid="checkout-name" required placeholder="Full name" className={field}
              value={form.customer_name} onChange={(e) => setForm({ ...form, customer_name: e.target.value })} />
            <input data-testid="checkout-email" required type="email" placeholder="Email address" className={field}
              value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} />
            <input data-testid="checkout-phone" required placeholder="M-Pesa phone e.g. 0754 000 000" className={field}
              value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} />
            {type === "order" && (
              <textarea data-testid="checkout-notes" placeholder="Tell us about your project (optional)" rows={3} className={field}
                value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} />
            )}
            <Button data-testid="checkout-continue-btn" type="submit" disabled={loading}
              className="w-full bg-brand-blue hover:bg-brand-bluehover text-white rounded-sm py-6 text-base">
              {loading ? <Loader2 className="animate-spin" /> : "Continue to get a Quote"}
            </Button>
          </form>
        )}

        {step === "mpesa" && (
          <div className="p-6 space-y-5" data-testid="checkout-mpesa-step">
            <div className="rounded-sm bg-[#E60000]/8 border border-[#E60000]/30 p-4 flex items-center gap-3">
              <div className="h-10 w-10 rounded-sm bg-[#E60000] flex items-center justify-center font-bold text-white text-xs font-heading">
                M-P
              </div>
              <div>
                <p className="font-semibold text-brand-dark">Pay with M-Pesa</p>
                <p className="text-xs text-gray-500">Lipa na M-Pesa · Tanzania</p>
              </div>
            </div>
            <div className="text-sm text-gray-600 space-y-2">
              <div className="flex justify-between"><span className="text-gray-400">Paying</span><span>{form.customer_name}</span></div>
              <div className="flex justify-between"><span className="text-gray-400">Phone</span><span>{form.phone}</span></div>
            </div>
            <Button data-testid="checkout-pay-btn" onClick={payWithMpesa} disabled={loading}
              className="w-full bg-[#E60000] hover:bg-[#c50000] text-white rounded-sm py-6 text-base flex items-center gap-2">
              {loading ? <Loader2 className="animate-spin" /> : <><Smartphone size={18} /> Send M-Pesa Prompt</>}
            </Button>
            <p className="text-xs text-gray-400 text-center flex items-center justify-center gap-1">
              <ShieldCheck size={13} /> Simulated checkout — no real charge.
            </p>
          </div>
        )}

        {step === "pending" && (
          <div className="p-8 flex flex-col items-center text-center gap-4" data-testid="checkout-pending-step">
            <div className="relative">
              <Loader2 size={54} className="animate-spin text-brand-blue" />
              <Smartphone size={22} className="absolute inset-0 m-auto text-brand-dark" />
            </div>
            <h4 className="text-xl font-heading text-brand-dark">Check your phone</h4>
            <p className="text-gray-500 text-sm max-w-xs">
              A payment prompt has been sent to <span className="text-brand-dark font-medium">{form.phone}</span>. Enter your M-Pesa PIN to authorize the request.
            </p>
          </div>
        )}

        {step === "success" && (
          <div className="p-8 flex flex-col items-center text-center gap-4" data-testid="checkout-success-step">
            <CheckCircle2 size={60} className="text-emerald-500" />
            <h4 className="text-2xl font-heading text-brand-dark">Payment Successful</h4>
            <p className="text-gray-500 text-sm">
              Thank you, {form.customer_name.split(" ")[0]}! Your {type === "order" ? "order" : "subscription"} is confirmed.
            </p>
            <div className="w-full rounded-sm bg-brand-grey border border-gray-200 p-4 text-sm space-y-2">
              <div className="flex justify-between"><span className="text-gray-400">M-Pesa Receipt</span><span className="font-mono text-brand-blue">{receipt}</span></div>
            </div>
            <Button data-testid="checkout-done-btn" onClick={() => onOpenChange(false)}
              className="w-full bg-brand-blue hover:bg-brand-bluehover text-white rounded-sm py-6">
              Done
            </Button>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
};
